import { useState, useCallback, useEffect, useRef } from 'react';
import { loadMetrics } from '@/store/metrics/actions';
import { useApiWithAuth } from '@/hooks/api';
import { useMetricsStore } from '@/store/metrics';
import { useLatestMetrics } from './computed';
import { useMetricsByDateRange } from './computed';
import { useDefaultCampus } from '../org/hooks';
import type { MetricType } from '@/shared/types/metrics';

export function useMetrics(type: MetricType) {
  const { api, auth } = useApiWithAuth();
  const store = useMetricsStore();
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);
  const campusId = useDefaultCampus();

  const loadMetricsData = useCallback(async () => {
    if (loaded.current || loading || !campusId) {
      return;
    }

    setLoading(true);
    loaded.current = true;

    try {
      const response = await loadMetrics({ api, auth, type, campusId });
      const data = { type, data: response };
      store.dispatch({ type: 'setMetrics', data });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, auth, type, loading, campusId, store]);

  useEffect(() => {
    if (type) {
      loadMetricsData();
    }
  }, [type, loadMetricsData]);

  const latest = useLatestMetrics(store, type);

  return {
    loading,
    metrics: store.state.data[type] || [],
    latest,
    loadMetrics: loadMetricsData,
  };
}

export function useMetricsByDate(
  type: MetricType,
  startDate: Date,
  endDate: Date,
) {
  const store = useMetricsStore();

  return useMetricsByDateRange(store, type, startDate, endDate);
}
